import { ApiError } from '@/core/api/request'
import { useApiRef } from '@/core/api/useApi'
import { MASTER_TABLE_TO_QUERY_KEYS, buildLocaleQueryKey, useQueryLocale } from '@/core/api/useTanstackFetchFn'
import { authV2Url } from '@/core/app/authUrls'
import { readIdTokenSubject } from '@/core/app/idTokenSubject'
import { logger } from '@/core/app/logger'
import { isValidCampaignSlug, useCampaignSlug } from '@/core/app/useCampaignSlug'
import { isSafeRedirectUrl } from '@/lib/url/isSafeRedirectUrl'
import { TokenRefreshSuccessSchema } from '@/schemas/v2/auth'
import type { CampaignData } from '@/schemas/v2/campaign'
import type { PopupItemData, PopupType } from '@/schemas/v2/popup'
import { useTokenStore } from '@/store/useTokenStore'
import { raundColors } from '@/theme/colors'
import { useQueryClient } from '@tanstack/react-query'
import { createActorContext } from '@xstate/react'
import { type ReactNode, useMemo, useRef } from 'react'
import { type SnapshotFrom, assign, fromPromise, setup } from 'xstate'
import {
  AuthFetchTimeoutError,
  type CheckAuthResult,
  checkAuthWithStore,
  fetchWithTimeout,
} from './raundLifeCycle/authFetch'
import { phase1Config } from './raundLifeCycle/phase1'
import { phase2ParallelConfig } from './raundLifeCycle/phase2Parallel'
import { phase3Config } from './raundLifeCycle/phase3'
import type { LifecycleContext, LifecycleEvent } from './raundLifeCycle/types'

const FALLBACK_PRIMARY = raundColors.grey
const FALLBACK_SECONDARY = raundColors.grey
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6})$/

const normalizeCampaignColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback
  const trimmed = color.trim()
  return HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback
}

const ONE_DAY_CACHE_TIME = 24 * 60 * 60 * 1000

/** refreshToken の最大リトライ回数（ネットワーク障害・5xx サーバーエラーを対象） */
const TOKEN_REFRESH_MAX_RETRIES = 3
/** リトライ時の指数バックオフ係数（ms）: attempt=0 → 5s、attempt=1 → 10s */
const TOKEN_REFRESH_BACKOFF_BASE_MS = 5000
const ONE_HOUR_MS = 60 * 60 * 1000

/** 5xx サーバーエラーを表す専用エラー型（文字列パースに依存しない instanceof によるリトライ判定に使用） */
export class TokenRefreshServerError extends Error {
  constructor(public readonly status: number) {
    super(`token/refresh server error: ${status}`)
    this.name = 'TokenRefreshServerError'
  }
}

/** 4xx クライアントエラーを表す専用エラー型（セッション期限切れなど認証起因エラーの識別に使用） */
export class TokenRefreshClientError extends Error {
  constructor(public readonly status: number) {
    super(`token/refresh client error: ${status}`)
    this.name = 'TokenRefreshClientError'
  }
}

/**
 * refreshToken API をリトライ付きで呼び出す。
 * - ネットワークエラー（TypeError）・タイムアウト・5xx サーバーエラーのみリトライ対象（最大 TOKEN_REFRESH_MAX_RETRIES 回）
 * - 4xx クライアントエラー・ZodError 等のパースエラーは即 throw してリトライしない
 * @internal テスト容易性のためモジュール関数として切り出し
 */
export async function refreshTokenWithRetry(fetchUrl: string): Promise<{ idToken: string; expiresAt: number }> {
  const logBase = { xstate: 'lifecycle' } as const
  let lastError: Error | undefined
  for (let attempt = 0; attempt < TOKEN_REFRESH_MAX_RETRIES; attempt++) {
    try {
      const res = await fetchWithTimeout(fetchUrl, { method: 'POST', credentials: 'include' })
      // 4xx はクライアント起因エラーのためリトライ不要。即 throw して onError に委ねる。
      if (!res.ok && res.status < 500) throw new TokenRefreshClientError(res.status)
      // 5xx は専用エラー型で throw してリトライ対象にする
      if (!res.ok) throw new TokenRefreshServerError(res.status)
      // JSON パース・スキーマ検証エラーはリトライで解決しないため即 throw
      const json: unknown = await res.json()
      const parsed = TokenRefreshSuccessSchema.parse(json)
      logger.info({ ...logBase, msg: 'refreshToken 完了' })
      return { idToken: parsed.idToken, expiresAt: parsed.expiresAt }
    } catch (e) {
      // 4xx・パースエラーは即 re-throw（リトライしない）。タイムアウトはネットワーク障害と同じ扱いでリトライする
      const isRetryable =
        e instanceof TypeError || e instanceof TokenRefreshServerError || e instanceof AuthFetchTimeoutError
      if (!isRetryable) throw e
      lastError = e
      if (attempt < TOKEN_REFRESH_MAX_RETRIES - 1) {
        const backoffMs = TOKEN_REFRESH_BACKOFF_BASE_MS * 2 ** attempt
        logger.warn({
          ...logBase,
          msg: `refreshToken エラー (attempt ${attempt + 1}/${TOKEN_REFRESH_MAX_RETRIES})、${backoffMs}ms 後にリトライ: ${String(e)}`,
        })
        await new Promise((resolve) => setTimeout(resolve, backoffMs))
      } else {
        logger.error({ ...logBase, msg: `refreshToken エラー (全 ${TOKEN_REFRESH_MAX_RETRIES} 回失敗): ${String(e)}` })
      }
    }
  }
  throw lastError ?? new Error('refreshToken failed: unexpected error')
}

export const raundLifeCycleMachineBase = setup({
  types: {
    context: {} as LifecycleContext,
    events: {} as LifecycleEvent,
  },
  actors: {
    // 実装は RaundLifeCycleProvider の provide() で注入される
    checkAuth: fromPromise(
      async (): Promise<{ isAuthenticated: boolean; idToken: string | null; expiresAt: number | null }> => {
        throw new Error('checkAuth not provided')
      },
    ),
    fetchMasterUpdate: fromPromise(async (): Promise<{ update: Record<string, boolean> }> => {
      throw new Error('fetchMasterUpdate not provided')
    }),
    fetchCampaign: fromPromise(async (): Promise<{ campaignData: CampaignData | null; notFound: boolean }> => {
      throw new Error('fetchCampaign not provided')
    }),
    fetchPopup: fromPromise(
      async (): Promise<{
        popup: Partial<Record<PopupType, PopupItemData[]>>
        isConfirmModalExists: boolean
      }> => {
        throw new Error('fetchPopup not provided')
      },
    ),
    fetchQuestionnaire: fromPromise(
      async (): Promise<{
        isLoginQuestionnaireExists: boolean
        questionnaire: LifecycleContext['questionnaire']
        isLoginQuestionnaireAnsweredByUser: boolean
      }> => {
        throw new Error('fetchQuestionnaire not provided')
      },
    ),
    refreshToken: fromPromise(async (): Promise<{ idToken: string; expiresAt: number }> => {
      throw new Error('refreshToken not provided')
    }),
    /**
     * idToken の読み込み完了（loading: false）を待つアクター。
     * checkAuth が setToken / clearToken を呼ぶと loading が false になり解決する。
     * provide() 不要（useTokenStore に直接依存）。
     */
    waitForTokenLoad: fromPromise(async (): Promise<void> => {
      if (!useTokenStore.getState().loading) return
      await new Promise<void>((resolve) => {
        const unsubscribe = useTokenStore.subscribe((state) => {
          if (!state.loading) {
            unsubscribe()
            resolve()
          }
        })
      })
    }),
  },
  actions: {
    clearToken: () => {
      useTokenStore.getState().clearToken()
    },
    /** loggedOut 進入時: context の認証情報をゲストに戻す（store 側は clearToken で空にする）（#3452） */
    assignGuestAuth: assign({
      isAuthenticated: false,
      idToken: null,
      expiresAt: null,
    }),
    // 実際の読み直しは RaundLifeCycleProvider の provide() で注入する（ユニットテストで location.reload を呼ばないため）
    reloadForMemberChange: () => {},
    saveConfirmClosed: ({ event }) => {
      if (event.type === 'CONFIRM_CLOSED' && event.skipNext && typeof window !== 'undefined') {
        // popupKey は Provider から受け取れないためここでは保存しない
        // saveConfirmClosed の実際の localStorage 書き込みは provide() で上書きされる
      }
    },
    assignAuthResult: assign(({ event }) => {
      const output = (
        event as unknown as {
          output: { isAuthenticated: boolean; idToken: string | null; expiresAt: number | null }
        }
      ).output
      return {
        isAuthenticated: output?.isAuthenticated ?? false,
        idToken: output?.idToken ?? null,
        expiresAt: output?.expiresAt ?? null,
      }
    }),
    assignCampaignResult: assign(({ event }) => {
      const output = (
        event as unknown as {
          output: { campaignData: CampaignData | null; notFound: boolean }
        }
      ).output
      const campaignData = output?.campaignData ?? null
      return {
        campaignData,
        campaignNotFound: output?.notFound ?? false,
        primaryColor: normalizeCampaignColor(campaignData?.mainColor ?? undefined, FALLBACK_PRIMARY),
        subColor: normalizeCampaignColor(campaignData?.subColor ?? undefined, FALLBACK_SECONDARY),
      }
    }),
    assignPopupResult: assign(({ event }) => {
      const output = (
        event as unknown as {
          output: {
            popup: Partial<Record<PopupType, PopupItemData[]>>
            isConfirmModalExists: boolean
          }
        }
      ).output
      return {
        popup: output?.popup ?? {},
        isConfirmModalExists: output?.isConfirmModalExists ?? false,
      }
    }),
    assignQuestionnaireResult: assign(({ event }) => {
      const output = (
        event as unknown as {
          output: {
            isLoginQuestionnaireExists: boolean
            questionnaire: LifecycleContext['questionnaire']
            isLoginQuestionnaireAnsweredByUser: boolean
          }
        }
      ).output
      return {
        isLoginQuestionnaireExists: output?.isLoginQuestionnaireExists ?? false,
        questionnaire: output?.questionnaire ?? null,
        isLoginQuestionnaireAnsweredByUser: output?.isLoginQuestionnaireAnsweredByUser ?? false,
      }
    }),
    saveRefreshedToken: assign(({ event }) => {
      const output = (event as unknown as { output: { idToken: string; expiresAt: number } }).output
      if (output?.idToken) {
        useTokenStore.getState().setToken(output.idToken, output.expiresAt)
      }
      return {
        idToken: output?.idToken ?? null,
        expiresAt: output?.expiresAt ?? null,
      }
    }),
  },
  guards: {
    isAuthenticated: ({ context }) => context.isAuthenticated,
    hasTokenInStore: ({ context }) => context.idToken !== null,
    needsQuestionnaire: ({ context }) =>
      context.isLoginQuestionnaireExists &&
      !context.isLoginQuestionnaireAnsweredByUser &&
      context.questionnaire !== null,
    needsConfirm: ({ context }) => context.isConfirmModalExists,
    /** トークンが期限切れ、または 60 秒以内に期限切れになる場合に true を返す */
    isTokenExpiredOrExpiring: ({ context }) => {
      if (!context.expiresAt) return false
      const nowSec = Math.floor(Date.now() / 1000)
      return context.expiresAt - nowSec <= 60
    },
    /**
     * NEW: token/refresh が 4xx 認証期限切れエラーで失敗したかどうかを判定するガード。
     * ネットワーク障害（TypeError）・5xx（TokenRefreshServerError）は false を返す。
     */
    isTokenRefreshAuthError: ({ event }) => {
      const error = (event as { error?: unknown }).error
      return error instanceof TokenRefreshClientError
    },
    /**
     * token/refresh が返した IdToken が、今の会員と別の会員のものか（#3760）。
     * 別タブで別アカウントに入り直していると、Cookie の RefreshToken は新しい会員のものになっている。
     * どちらかの sub が読めないときは false（今までどおり保存する）。判定できないことを理由に読み直しを繰り返さないため。
     */
    isRefreshedForAnotherMember: ({ context, event }) => {
      const output = (event as unknown as { output?: { idToken?: string } }).output
      if (!context.idToken || !output?.idToken) return false
      const current = readIdTokenSubject(context.idToken)
      const refreshed = readIdTokenSubject(output.idToken)
      return current !== null && refreshed !== null && current !== refreshed
    },
  },
  delays: {
    preRefreshDelay: ({ context }) => {
      if (!context.expiresAt) return Number.MAX_SAFE_INTEGER
      const nowSec = Math.floor(Date.now() / 1000)
      return Math.max(0, (context.expiresAt - 60 - nowSec) * 1000)
    },
    masterUpdateInterval: ONE_HOUR_MS,
  },
}).createMachine({
  id: 'lifecycle',
  initial: 'phase1',
  context: {
    // contextには、primitiveな値のみ保持する
    // isRenderingReadyのような複合的な状態は保持しない。
    // phase 1で得られる結果
    isAuthenticated: false,
    idToken: null,
    expiresAt: null,
    campaignData: null,
    campaignNotFound: false,
    isConfirmModalExists: false,
    primaryColor: FALLBACK_PRIMARY,
    subColor: FALLBACK_SECONDARY,
    popup: {},
    // masterUpdate は phase1 で副作用（queryClient.invalidateQueries）のみ実行するためここでは保持しない
    // phase 2で得られる結果
    questionnaire: null,
    isLoginQuestionnaireExists: false,
    isLoginQuestionnaireAnsweredByUser: false,
  },
  states: {
    // phase1 と phase2ではスケルトンを表示する
    phase1: phase1Config,
    phase2Parallel: phase2ParallelConfig,
    // phase3でページ固有の状態を表示・処理を開始する
    phase3: phase3Config,
    /**
     * セッション切れ（トークンリフレッシュの 4xx）・LOGOUT でゲストに戻った安定状態（#3452）。
     * ページはゲストとして表示する（selectLifecycleLoadingStatus は loaded）。
     * ページ固有の自動処理は始めない（selectIsQuestionnaireSettled は false）。
     */
    loggedOut: {
      entry: ['clearToken', 'assignGuestAuth'],
      on: {
        /**
         * 安全弁: タブが再び表示されたとき、phase1 から再認証を試みる。
         * 4xx（セッション期限切れ）で loggedOut に来た場合は checkAuth が未認証を返し、
         * ゲストとしてページが正常表示される。
         * 想定外の経路で loggedOut に到達していた場合（一時的エラーなど）は再認証に成功する。
         */
        PAGE_VISIBLE: 'phase1' as const,
      },
    },
    /**
     * token/refresh で別の会員のトークンが返ってきた（#3760）。entry で読み直すので、ここから先へは遷移しない。
     * phase3 を抜けるため、読み直しまでの間はスケルトン表示になり、ページ固有の自動処理（スタンプ自動獲得等）も止まる。
     */
    memberChanged: {
      entry: 'reloadForMemberChange',
    },
  },
})

export const AppMachineContext = createActorContext(raundLifeCycleMachineBase)

/**
 * Raundの各ページが描画される前に実行されるべき内容をまとめたコンポーネント。
 * 全ページに影響があるコードのみここに記載するようにする
 * */
export const RaundLifeCycleProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()
  const { campaignSlug, LOCAL_STORAGE_POPUP_HOOKPOINT_KEY, gen } = useCampaignSlug()
  // ⚠️ campaign / popups の queryKey は useTanstackFetchFn 側と同じ形でなければならない。
  // 片方だけロケールが欠けると同じ API が 2 つのキーで二重フェッチされるため、
  // キーの組み立ては必ず buildLocaleQueryKey() を通すこと（#3529）。
  const locale = useQueryLocale()
  const popupKey = LOCAL_STORAGE_POPUP_HOOKPOINT_KEY('home')

  const apiRef = useApiRef()

  const popupKeyRef = useRef(popupKey)
  popupKeyRef.current = popupKey

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs to access latest values
  const providedMachine = useMemo(
    () =>
      raundLifeCycleMachineBase.provide({
        actors: {
          checkAuth: fromPromise(async (): Promise<CheckAuthResult> => {
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない。
            // store にも触らない（ここで clearToken すると slug 確定前に /qr が未ログインと誤判定しうる）
            if (!isValidCampaignSlug(campaignSlug)) {
              return { isAuthenticated: false, idToken: null, expiresAt: null }
            }
            return checkAuthWithStore(campaignSlug, () => {
              // ログイン確定時: ゲスト/旧セッションの sheets/spots キャッシュを削除し、購入連動ロック状態を再取得させる
              queryClient.removeQueries({ queryKey: ['sheets'] })
              queryClient.removeQueries({ queryKey: ['spots'] })
              // 別タブで別アカウントにログインし直した場合など、リロードなしで会員が入れ替わることがある。
              // 旧会員の id・前回のログイン（#3635）をメニューに出さないよう、プロフィールも取り直させる
              queryClient.removeQueries({ queryKey: ['userProfile'] })
              // お知らせの既読状態は会員ごとに異なるため、一覧・詳細・未読バッジのキャッシュも取り直させる（#2973）
              queryClient.removeQueries({ queryKey: ['notifications'] })
              queryClient.removeQueries({ queryKey: ['notificationsUnread'] })
            })
          }),

          fetchMasterUpdate: fromPromise(async () => {
            const logBase = { xstate: 'lifecycle' } as const
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない
            if (!isValidCampaignSlug(campaignSlug)) {
              return { update: {} }
            }
            logger.info({ ...logBase, msg: 'fetchMasterUpdate 開始' })
            try {
              const res = await apiRef.current.guest.fetchMasterUpdateCheck()
              if (!res?.update) {
                logger.info({ ...logBase, msg: 'fetchMasterUpdate 完了（更新なし）' })
                return { update: {} }
              }

              // 更新が必要なテーブルに対応する TanStack Query キャッシュを無効化
              for (const [tableName, needsUpdate] of Object.entries(res.update)) {
                if (!needsUpdate) continue
                const queryKeys = MASTER_TABLE_TO_QUERY_KEYS[tableName]
                if (!queryKeys) continue
                for (const key of queryKeys) {
                  queryClient.invalidateQueries({ queryKey: [key] })
                }
              }
              logger.info({ ...logBase, msg: 'fetchMasterUpdate 完了' })
              return { update: res.update }
            } catch (e) {
              logger.error({ ...logBase, msg: `fetchMasterUpdate エラー: ${String(e)}` })
              throw e
            }
          }),

          fetchCampaign: fromPromise(async (): Promise<{ campaignData: CampaignData | null; notFound: boolean }> => {
            const logBase = { xstate: 'lifecycle' } as const
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない
            if (!isValidCampaignSlug(campaignSlug)) {
              return { campaignData: null, notFound: false }
            }
            logger.info({ ...logBase, msg: 'fetchCampaign 開始' })
            try {
              const result = await queryClient.fetchQuery({
                // useTanstackCampaignFetchFn と同一のキーになるよう共通ビルダーを使う（#3529）
                queryKey: buildLocaleQueryKey('campaign', campaignSlug, locale),
                // 起動のたびに取り直す（#3739）。campaignStartAt / campaignEndAt はテストアクセスの有無・会期の合間で
                // API が値を変えて返す（#3735）ため、キャッシュを使うとテスト開始前に開いていたブラウザが「開始前」のまま、
                // 会期の合間に「終了」のままになる。ページ側（useTanstackCampaignFetchFn）はここで書いた値を使うので 1 日のままでよい。
                // 現状は永続キャッシュの復元より先にここが走るため 1 日でも取り直しているが、その順序に依存しないよう明示する
                staleTime: 0,
                queryFn: () => apiRef.current.guest.fetchCampaign(),
              })

              const campaignData = result?.campaign ?? null

              // redirectUrl が存在し安全な場合はリダイレクト（ページ描画を行わない）
              if (
                campaignData?.redirectUrl &&
                typeof window !== 'undefined' &&
                isSafeRedirectUrl(campaignData.redirectUrl)
              ) {
                logger.info({
                  ...logBase,
                  msg: `fetchCampaign: redirectUrl へリダイレクト → ${campaignData.redirectUrl}`,
                })
                window.location.replace(campaignData.redirectUrl)
              }

              logger.info({ ...logBase, msg: 'fetchCampaign 完了' })
              return { campaignData, notFound: false }
            } catch (e) {
              // 404 はキャンペーン不存在。throw せず notFound=true で onDone に倒し、404 ビューを表示させる。
              if (e instanceof ApiError && e.status === 404) {
                logger.info({ ...logBase, msg: 'fetchCampaign: キャンペーン不存在（404）' })
                return { campaignData: null, notFound: true }
              }
              // ネットワークエラー・5xx 等は従来どおり throw（phase1 campaign.failed へ）
              logger.error({ ...logBase, msg: `fetchCampaign エラー: ${String(e)}` })
              throw e
            }
          }),

          fetchQuestionnaire: fromPromise(async () => {
            const logBase = { xstate: 'lifecycle' } as const
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない
            if (!isValidCampaignSlug(campaignSlug)) {
              return {
                isLoginQuestionnaireExists: false,
                questionnaire: null,
                isLoginQuestionnaireAnsweredByUser: false,
              }
            }
            logger.info({ ...logBase, msg: 'fetchQuestionnaire 開始' })
            try {
              const res = await apiRef.current.auth.fetchQuestionnairesType('register')
              const questionnaire = res.questionnaire ?? null
              const answered = questionnaire?.answered ?? false
              logger.info({
                ...logBase,
                msg: `fetchQuestionnaire 完了 (exists=${questionnaire !== null}, answered=${answered})`,
              })
              return {
                isLoginQuestionnaireExists: questionnaire !== null,
                // 回答済みの場合はダイアログを表示しない
                questionnaire: answered ? null : questionnaire,
                isLoginQuestionnaireAnsweredByUser: answered,
              }
            } catch (e) {
              logger.error({ ...logBase, msg: `fetchQuestionnaire エラー: ${String(e)}` })
              throw e
            }
          }),

          refreshToken: fromPromise(async () => {
            const logBase = { xstate: 'lifecycle' } as const
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない
            if (!isValidCampaignSlug(campaignSlug)) {
              throw new Error('campaignSlug is not ready')
            }
            logger.info({
              ...logBase,
              fetch_method: 'POST',
              fetch_url: authV2Url.tokenRefresh(campaignSlug),
              msg: 'refreshToken 開始',
            })
            return refreshTokenWithRetry(authV2Url.tokenRefresh(campaignSlug))
          }),

          fetchPopup: fromPromise(async () => {
            const logBase = { xstate: 'lifecycle' } as const
            // campaignSlug が未確定（hydration 前の '[campaign]' または空文字）の場合は API を叩かない
            if (!isValidCampaignSlug(campaignSlug)) {
              return { popup: {}, isConfirmModalExists: false }
            }
            logger.info({ ...logBase, msg: 'fetchPopup 開始' })
            const popupRecord: Partial<Record<PopupType, PopupItemData[]>> = {}

            try {
              const result = await queryClient.fetchQuery({
                // ポップアップの画像・本文は Translatable（PopupImageRepository が
                // TranslationWalker を適用）のためロケール別にキャッシュを分ける（#3529）
                queryKey: buildLocaleQueryKey('popups', campaignSlug, locale),
                staleTime: ONE_DAY_CACHE_TIME,
                queryFn: () => apiRef.current.guest.fetchPopups(),
              })

              if (result?.popup) {
                for (const typeData of result.popup) {
                  popupRecord[typeData.type] = typeData.item
                }
              }
            } catch (e) {
              logger.error({ ...logBase, msg: `fetchPopup エラー: ${String(e)}` })
              // 失敗時は空オブジェクトのまま続行（ポップアップ非表示）
            }

            // HOME ポップアップ存在 & localStorage 未設定の場合のみ確認モーダル表示
            const isConfirmModalExists =
              typeof window !== 'undefined' &&
              localStorage.getItem(popupKeyRef.current) !== '1' &&
              (popupRecord.HOME?.length ?? 0) > 0

            logger.info({ ...logBase, msg: `fetchPopup 完了 (isConfirmModalExists=${isConfirmModalExists})` })
            return { popup: popupRecord, isConfirmModalExists }
          }),
        },
        actions: {
          saveConfirmClosed: ({ event }) => {
            if (event.type === 'CONFIRM_CLOSED' && event.skipNext && typeof window !== 'undefined') {
              localStorage.setItem(popupKeyRef.current, '1')
            }
          },
          // token/refresh で別の会員のトークンが返ってきたとき（#3760）。前の会員のキャッシュ・ページ状態を作り直す代わりに読み直す。
          // AR カメラ表示中の抑止（pageReloadSuppressed）は見ない。会員の取り違えは正しさの問題なので優先する
          reloadForMemberChange: () => {
            logger.info({ xstate: 'lifecycle', msg: 'refreshToken: 別の会員のトークンが返ったためページを読み直す' })
            window.location.reload()
          },
          // ログアウト時: sheets/spots キャッシュを削除してからトークンをクリアする（購入連動ロック状態の持ち越しを防ぐ）。
          // プロフィール（id・前回のログイン #3635）も、次にログインした別の会員へ持ち越さないよう削除する。
          // お知らせの既読状態（一覧・詳細・未読バッジ）も会員固有のため同様に削除する（#2973）
          clearToken: () => {
            queryClient.removeQueries({ queryKey: ['sheets'] })
            queryClient.removeQueries({ queryKey: ['spots'] })
            queryClient.removeQueries({ queryKey: ['userProfile'] })
            queryClient.removeQueries({ queryKey: ['notifications'] })
            queryClient.removeQueries({ queryKey: ['notificationsUnread'] })
            useTokenStore.getState().clearToken()
          },
        },
      }),
    [campaignSlug],
  )

  return <AppMachineContext.Provider logic={providedMachine}>{children}</AppMachineContext.Provider>
}

type RaundLifeCycleSnapshot = SnapshotFrom<typeof raundLifeCycleMachineBase>

/**
 * 会員登録時アンケート（type=register）の表示判定が済み、ページ固有の自動処理を始めてよいか（#3649）。
 *
 * - `showingQuestionnaire` の間はアンケートの裏で自動処理（スタンプ自動獲得など）を走らせてはならない。
 * - `showingConfirm` は「進んでよい」に含める。確認ポップアップはホームでしか描画されず、
 *   `CONFIRM_CLOSED` もホームしか送らないため、ここを待つとホーム以外のページで永久に待つことになる。
 * - phase1 / phase2 は false（アンケートの要否がまだ確定していない）。
 * - `loggedOut`（トークンリフレッシュの 4xx 失敗）も false（ページはゲストとして表示するが、自動処理は始めない）。
 *   セッションが切れたまま自動押印を始めず、`PAGE_VISIBLE` で phase1 から再認証されるまで待つ。
 *   手動の獲得ボタンはスポット詳細のスタンプマシンが押下時点のトークンで判定し、未ログイン案内を出す（#3654）。
 *
 * テストしやすいよう純関数として公開し、フックはこれを useSelector に渡すだけにする。
 */
export const selectIsQuestionnaireSettled = (s: RaundLifeCycleSnapshot): boolean =>
  s.matches({ phase3: { flow: 'appReady' } }) || s.matches({ phase3: { flow: 'showingConfirm' } })

/**
 * @example
 * const isQuestionnaireSettled = useIsQuestionnaireSettled()
 * useEffect(() => { if (!isQuestionnaireSettled) return; ... }, [isQuestionnaireSettled])
 */
export const useIsQuestionnaireSettled = (): boolean => AppMachineContext.useSelector(selectIsQuestionnaireSettled)

/**
 * ライフサイクルのロード状態。
 * - phase1 / phase2: loading（スケルトン）
 * - phase3 の appReady / showingConfirm: loaded（終端状態の列挙は selectIsQuestionnaireSettled に集約し、ここで二重管理しない）
 * - phase3 の showingQuestionnaire: loaded（アンケートのダイアログの下にページ本体を描画する）
 * - loggedOut: loaded（ゲストとして表示する。loading にするとタブ切替まで抜けられずスケルトンが固着する #3452）
 *
 * テストしやすいよう純関数として公開する。
 */
export const selectLifecycleLoadingStatus = (s: RaundLifeCycleSnapshot): 'loading' | 'loaded' =>
  selectIsQuestionnaireSettled(s) || s.matches({ phase3: { flow: 'showingQuestionnaire' } }) || s.matches('loggedOut')
    ? 'loaded'
    : 'loading'

/**
 * raundのLifeCycleにおいて、ローディングが完了しているかどうかをステータスで返す。
 * status:= loadingの場合には、スケルトンを表示する。
 * status:= loadedの場合には、各ページの内容を表示するようにする。
 * */
export const useRaundLifeCycleLoadingStatus = (): { status: 'loading' | 'loaded' | 'failed' } => {
  const status = AppMachineContext.useSelector(selectLifecycleLoadingStatus)
  return { status }
}

/**
 * phase1 の認証確認（checkAuth）が実行中か（#3654）。
 *
 * `loggedOut` から `PAGE_VISIBLE` で認証をやり直している間、`useTokenStore` は `loading: false`・`idToken: null` のままで、
 * 未認証と区別できない。認証の結果で動作が変わる操作（スタンプ獲得など）は、これが true の間は受け付けない。
 * checkAuth が終われば store は setToken / clearToken で確定するため、phase1 の他の取得（campaign / popup / masterUpdate）や
 * phase2 は待たない。
 *
 * テストしやすいよう純関数として公開する。
 */
export const selectIsAuthChecking = (s: RaundLifeCycleSnapshot): boolean => s.matches({ phase1: { auth: 'checking' } })

/** @see selectIsAuthChecking */
export const useIsAuthChecking = (): boolean => AppMachineContext.useSelector(selectIsAuthChecking)

/**
 * 指定タイプのポップアップが存在するか返すセレクタフック。
 * 各ページがポップアップ表示の要否を確認するために使用する。
 *
 * @example
 * // SPOTS ページでの使用例
 * const hasPopup = usePopupExists('SPOTS')
 */
export const usePopupExists = (type: PopupType): boolean => {
  return AppMachineContext.useSelector((s) => (s.context.popup?.[type]?.length ?? 0) > 0)
}

/**
 * 指定タイプのポップアップアイテム一覧を返すセレクタフック。
 *
 * @example
 * const items = usePopupItems('HOME')
 * // items: Array<{ image: string | null, url: string | null }>
 */
export const usePopupItems = (type: PopupType): PopupItemData[] => {
  return AppMachineContext.useSelector((s) => s.context.popup?.[type] ?? [])
}
